import { Temporal } from '@imago/api-client'
import { GAHT_CARE_PAYMENT_DESCRIPTIONS } from './reports/Payment.ts'
import { type Stripe_InvoiceID, type Stripe_PaymentIntentID } from '@imago/api-client-stripe'
import { type IS_Customer, type IS_Invoice, type IS_PaymentIntent } from './stripe.ts'
import { type UserID } from './documents/_EMR.ts'

interface Invoice {
	start: Temporal.ZonedDateTime
	end: Temporal.ZonedDateTime
	EURcents: number
	paidOn: Temporal.ZonedDateTime | null
}

export const CARE_PRICE_SCHEDULE = [
	{
		from: Temporal.ZonedDateTime.from('1970-01-01T00:00:00[Europe/Malta]'),
		EURcentsPerMonth: 2000,
	},
	{
		from: Temporal.ZonedDateTime.from('2026-05-31T00:00:00[Europe/Malta]'),
		EURcentsPerMonth: 2200,
	},
] as const

export const CURRENT_CARE_MONTHLY_PRICE_EURCENTS = CARE_PRICE_SCHEDULE[CARE_PRICE_SCHEDULE.length - 1]!.EURcentsPerMonth

export interface CareBreakdown {
	started: Temporal.ZonedDateTime
	terminated: Temporal.ZonedDateTime | null
	freeMonthEnded: Temporal.ZonedDateTime
	paidUntil: Temporal.ZonedDateTime
	invoicedUntil: Temporal.ZonedDateTime
	nextCharge: Temporal.ZonedDateTime | null
	today: Temporal.ZonedDateTime
	pastInvoices?: Invoice[]
}

export interface CarePaymentsDecision {
	breakdown: CareBreakdown | null
	issueInvoice?: Invoice & {paidOn: null}
	issueSubscription?: {
		start?: Temporal.ZonedDateTime
	}
	status: string
	overchargedMonths?: number
}

function addMonths(date: Temporal.ZonedDateTime, months: number) {
	// Temporal.Duration does not support decimal months :(
	const integerMonths = Math.floor(months)
	const remainingMonths = months - integerMonths
	const thisMonth = date.add({months: integerMonths})
	const secondsUntilNextMonth = date.add({months: integerMonths + 1}).since(thisMonth).total('seconds')
	const remainingSeconds = Math.floor(remainingMonths * secondsUntilNextMonth)
	return thisMonth.add({seconds: remainingSeconds})
}

function carePriceAt(date: Temporal.ZonedDateTime) {
	return CARE_PRICE_SCHEDULE.toReversed().find(price => Temporal.ZonedDateTime.compare(price.from, date) <= 0)!
}

function nextCarePriceChangeAfter(date: Temporal.ZonedDateTime) {
	return CARE_PRICE_SCHEDULE.find(price => Temporal.ZonedDateTime.compare(price.from, date) > 0)?.from
}

function minZonedDateTime(a: Temporal.ZonedDateTime, b: Temporal.ZonedDateTime) {
	return Temporal.ZonedDateTime.compare(a, b) <= 0 ? a : b
}

function getCareWaiverEURcents(customer: IS_Customer | null) {
	const rawWaiver = customer?.metadata.careWaiverEURcents
	if (rawWaiver === undefined || rawWaiver === '') return 0

	if (!/^\d+$/.test(rawWaiver)) {
		throw new Error(`Invalid careWaiverEURcents: ${rawWaiver}`)
	}
	const waiver = Number(rawWaiver)
	if (!Number.isSafeInteger(waiver)) {
		throw new Error(`Invalid careWaiverEURcents: ${rawWaiver}`)
	}
	return waiver
}

function rawCarePriceEURcentsForPeriod(start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime) {
	let cursor = start
	let total = 0
	while (Temporal.ZonedDateTime.compare(cursor, end) < 0) {
		const segmentEnd = nextCarePriceChangeAfter(cursor)
		const periodEnd = segmentEnd ? minZonedDateTime(segmentEnd, end) : end
		total += periodEnd.since(cursor).total({unit: 'months', relativeTo: cursor}) * carePriceAt(cursor).EURcentsPerMonth
		cursor = periodEnd
	}
	return total
}

export function carePriceEURcentsForPeriod(start: Temporal.ZonedDateTime, end: Temporal.ZonedDateTime) {
	return Math.floor(rawCarePriceEURcentsForPeriod(start, end))
}

function addMoney(date: Temporal.ZonedDateTime, {EURcents}: {EURcents: number}) {
	let cursor = date
	let remaining = EURcents
	while (remaining > 0) {
		const price = carePriceAt(cursor).EURcentsPerMonth
		const nextChange = nextCarePriceChangeAfter(cursor)
		if (!nextChange) {
			return addMonths(cursor, remaining / price).round({smallestUnit: 'second', roundingMode: 'trunc'})
		}
		const costUntilNextChange = rawCarePriceEURcentsForPeriod(cursor, nextChange)
		if (remaining < costUntilNextChange) {
			return addMonths(cursor, remaining / price).round({smallestUnit: 'second', roundingMode: 'trunc'})
		}
		remaining -= costUntilNextChange
		cursor = nextChange
	}
	return cursor.round({smallestUnit: 'second', roundingMode: 'trunc'})
}

const skipIDs = new Set<UserID>([
	'1TEvdvcXLY98z3MRY2tMIbARUjtS1CESf',
	'1PBhhY4RtEzSl3NHGdPYZvmDUokKVimMc',
	'1yTCOteikgZV6Q7amlvxYZw6_CBnc3aUS',
	'1kBFTvggFZXHj12EIfLlw0-SiVp6dq01P',
	'1KQ_wMj5CseEjzq0NVgUtCxV1i4jCMu10',
	'1IMwbICqXxy9PITioQFbGVvKzAO9tLrv1',
	'1c1RG-H-9ggT6jTE21udtrQZcK2ZYY8ZL',
	'1CWYgnp5NQOg363HqLYz0nwLfoZ9WPmO3',
	'1TQeDQ51b1mC8TmeyNgkRQ5mHaZRRi7Ot',
	'1mvRWoVkK_vDaWSSZO3Sa9rjDKOdF340F',
	'1-mMcXUmGjRC9Yrx0pu7vqWw7tDs8iCn8',
	'1kcufAJLUBP-42KKAjq0TaF-iN_8yDMAw',
	'1dU6KKhc5rol8Iw8h-8mq4cTqFukxJRnB',
	'1-RPYRWxh8gQ1s2olBSoLNoExyn3LKe9p',
	'1COFDUoJBveh-aReci28Kx5SewD_usjqp',
	'1eDPxZVobohG7mmPEI5EjbkJhypv20ola',
	'1qSn9cC4MR4kABbwZANd54vAKiemGllc7',
	'1X_-aW9IoGCSp0aS2JwYxvZlaouuTGU5d',
	'14TcaKn3FOgxP4Ss-zXBDDBbPYKlYD_hj',
	'1DAqMrfu4m_wjV0b2fiyoZPu9_yIYph7C',
	'1rpjYAZO7jJHLzMaZA-H65-LgKu2Oi4nV',
	'1u9bkbxIr1ihAS6azJY8IjJR5yLhhQNjB',
	'1fDspPuykzWq0jfbVRM0IwROCUhT71ZNp',
] as UserID[])

export function decideAboutCarePayments({myCareTS, myCareTerminatedTS, myCustomer, myPayments, myOpenInvoices, myNextChargeTS, nowTS, withMyJustStatedIntentToBeUnderCare}: {
	myCareTS: Temporal.Instant
	myCareTerminatedTS?: Temporal.Instant | null
	myNextChargeTS: Temporal.Instant | null
	myCustomer: IS_Customer | null
	myPayments: Map<Stripe_PaymentIntentID, IS_PaymentIntent>
	myOpenInvoices: Map<Stripe_InvoiceID, IS_Invoice>
	nowTS: Temporal.Instant
	withMyJustStatedIntentToBeUnderCare?: boolean
} & (
	{withMyJustStatedIntentToBeUnderCare: true} | {myCareTerminatedTS: Temporal.Instant | null}
)): CarePaymentsDecision {
	if ([...myOpenInvoices.values().filter(i => i.recorded_customer_id !== myCustomer!.id)].length) {
		throw new Error('myOpenInvoices should be only directly recorded invoices!')
	}

	const myCareStarted = myCareTS.toZonedDateTimeISO('Europe/Malta').round({smallestUnit: 'second', roundingMode: 'trunc'})

	const iCouldHaveHadFreeMonth = Temporal.ZonedDateTime.compare(myCareStarted, Temporal.ZonedDateTime.from('2025-09-01T00:00:00[Europe/Malta]')) < 0
	const myCareFreeMonthEnded = iCouldHaveHadFreeMonth ? myCareStarted.add({months: 1}) : myCareStarted
	const myCarePayments = [...myPayments.values().filter(p => p.description && GAHT_CARE_PAYMENT_DESCRIPTIONS.has(p.description))]
	const myCarePaymentsSum = myCarePayments.map(p => p.amount).reduce((acc, v) => acc + v, 0)
	const myCarePaidUntil = addMoney(myCareFreeMonthEnded, {EURcents: myCarePaymentsSum}).round({smallestUnit: 'second', roundingMode: 'trunc'})

	const myCareInvoicesSum = myOpenInvoices.values().map(p => p.amount_remaining).reduce((acc, v) => acc + v, 0)
	const myCareWaiverSum = getCareWaiverEURcents(myCustomer)
	const paidOrInvoicedSum = myCarePaymentsSum + myCareInvoicesSum + myCareWaiverSum
	const myCareInvoicedUntil = addMoney(myCareFreeMonthEnded, {EURcents: paidOrInvoicedSum})

	const myCareNextCharge = myNextChargeTS?.toZonedDateTimeISO('Europe/Malta') ?? null
	const myCareTerminated = withMyJustStatedIntentToBeUnderCare ? null :
		myCareTerminatedTS?.toZonedDateTimeISO('Europe/Malta') ?? null

	// Subtract 10 seconds from now, because Stripe crashes if backdate_start_time = now.
	const now = nowTS.toZonedDateTimeISO('Europe/Malta').round({smallestUnit: 'second', roundingMode: 'trunc'}).subtract({seconds: 10})

	const myCareOrderedUntil = myCareTerminated ?? myCareNextCharge ?? now

	const correctSum = carePriceEURcentsForPeriod(myCareFreeMonthEnded, myCareOrderedUntil)
	const unallocatedSum = correctSum - paidOrInvoicedSum

	const totalUnallocatedDays = myCareOrderedUntil.since(myCareInvoicedUntil).total({unit: 'days', relativeTo: myCareInvoicedUntil})
	const totalUnallocatedMonths = myCareOrderedUntil.since(myCareInvoicedUntil).total({unit: 'months', relativeTo: myCareInvoicedUntil})

	const myPastInvoices = new Array<Invoice>()
	let invoicedBefore = {EURcents: 0}
	for (const payment of myCarePayments.toSorted((a, b) => a.latest_charge!.created - b.latest_charge!.created)) {
		const invoicedWithThisOne = {EURcents: invoicedBefore.EURcents + payment.amount}
		myPastInvoices.push({
			start: addMoney(myCareFreeMonthEnded, invoicedBefore),
			end: addMoney(myCareFreeMonthEnded, invoicedWithThisOne),
			EURcents: payment.amount,
			paidOn: Temporal.Instant.fromEpochMilliseconds(payment.latest_charge!.created * 1000).toZonedDateTimeISO('Europe/Malta'),
		})
		invoicedBefore = invoicedWithThisOne
	}
	for (const openInvoice of myOpenInvoices.values()) {
		const invoicedWithThisOne = {EURcents: invoicedBefore.EURcents + openInvoice.amount_due}
		myPastInvoices.push({
			start: addMoney(myCareFreeMonthEnded, invoicedBefore),
			end: addMoney(myCareFreeMonthEnded, invoicedWithThisOne),
			EURcents: openInvoice.amount_due,
			paidOn: null,
		})
	}
	const breakdown: CareBreakdown = {
		started: myCareStarted,
		terminated: myCareTerminated,
		freeMonthEnded: myCareFreeMonthEnded,
		paidUntil: myCarePaidUntil,
		invoicedUntil: myCareInvoicedUntil,
		nextCharge: myCareNextCharge,
		today: now,
		pastInvoices: myPastInvoices,
	}

	if (totalUnallocatedMonths <= 0) {
		// They were overcharged (ie. voluntarily subscribed for more time than they're under care)
		// No problem for us :P
		return {
			breakdown,
			status: `overcharged`,
			overchargedMonths: -totalUnallocatedMonths,
			issueSubscription: !myCareTerminated && !myNextChargeTS ? {} : undefined,
		}
	} else if (!myCareTerminated && !myNextChargeTS && totalUnallocatedDays < 20) {
		// We can fix that by moving the next charge.
		return {
			breakdown,
			status: `move the next charge`,
			overchargedMonths: -totalUnallocatedMonths,
			issueSubscription: {start: myCareInvoicedUntil},
		}
	} else if (totalUnallocatedDays < 20) {
		// We don't want to trouble people when it's not worth it.
		return {
			breakdown,
			status: `give a free lunch`,
			overchargedMonths: -totalUnallocatedMonths,
		}
	} else if (!withMyJustStatedIntentToBeUnderCare && !myNextChargeTS && totalUnallocatedMonths < 1.2) {
		// If somebody has not set up auto payments, we should just message them every month.
		// Note that this is not yet implemented.
		// Still, there is no point in going thru this path for them.
		return {
			breakdown,
			status: `give a free lunch (for now)`,
			overchargedMonths: -totalUnallocatedMonths,
		}
	} else if (!withMyJustStatedIntentToBeUnderCare && totalUnallocatedMonths < 1.2) {
		// Will handle them in the next wave.
		return {
			breakdown,
			status: `give a free lunch (for now)`,
			overchargedMonths: -totalUnallocatedMonths,
		}
	} else if (!withMyJustStatedIntentToBeUnderCare && myCareTerminated) {
		// TODO handle terminated care
		return {
			breakdown,
			status: `give a free lunch (because terminated)`,
			overchargedMonths: -totalUnallocatedMonths,
		}
	} else if (!withMyJustStatedIntentToBeUnderCare && myCustomer && skipIDs.has(myCustomer.metadata.userID)) {
		return {
			breakdown,
			status: `give a free lunch (because status unknown)`,
			overchargedMonths: -totalUnallocatedMonths,
		}
	} else {
		// We need to issue an invoice for the unpaid time.
		const invoiceFor = unallocatedSum
		const myCareWillBeInvoicedUntil = addMoney(myCareFreeMonthEnded, {EURcents: paidOrInvoicedSum + invoiceFor})
		if (Math.abs(myCareWillBeInvoicedUntil.since(breakdown.terminated ?? breakdown.nextCharge ?? breakdown.today).total('hours')) >= 1) throw new Error(`Programming error`)
		return {
			breakdown,
			status: `issue an invoice`,
			overchargedMonths: -totalUnallocatedMonths,
			issueInvoice: {start: myCareInvoicedUntil, end: myCareWillBeInvoicedUntil, EURcents: invoiceFor, paidOn: null},
			issueSubscription: !myCareTerminated && !myNextChargeTS ? {start: myCareWillBeInvoicedUntil} : undefined,
		}
	}
}
